/** Rich text (the tour itinerary's day and stop descriptions, the tour plan §5
 *  `richtext`): the stored HTML, read in place, until it is clicked (or focused and Enter /
 *  Space) — then the shared `QuillEditor`, seeded once with the value (uncontrolled; its
 *  semantic export with `plainSpaces`, and only `source === 'user'` edits are reported —
 *  P0's decision).
 *
 *  - No Quill is mounted at rest: a day with eight stops opens with no editor at all.
 *  - Edits commit after a 300 ms pause or when focus leaves the field (its toolbar and link
 *    tooltip are inside it), and one focus session is ONE undo step (one coalesce token
 *    from open to close).
 *  - Mounting reports nothing: stored plain-space HTML is never read as an edit.
 *  - When focus leaves, the field goes back to its HTML; an outside change while it is open
 *    (an undo from the ledger) re-seeds the editor (QuillEditor's own rule).
 *  - Opening moves no text: the editor takes the closed box's place and height, its
 *    toolbar floats above it (over the label row), and the caret lands where the text was
 *    clicked (Enter / Space: at the end). Escape closes it and gives the focus back to the
 *    closed box.
 *  - Named in both states: the closed box is the button "Edit <label>"; the open editor is
 *    a multi-line textbox named <label>, described by the same caption (`describedBy`) and
 *    invalid with `error`. `onSessionEnd` follows a session's last commit (the record
 *    editor's FieldSlot closes its undo step there). */
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { HtmlContent } from '@/components/RichText/HtmlContent';
import { isHtmlEmpty, QuillEditor, type QuillInstance } from '@/components/RichText/QuillEditor';

export const RICHTEXT_COMMIT_MS = 300;

/** The read-only box's geometry, shared by view mode, the closed editor and the open one
 *  (a 1 px border — transparent until open —, 8 / 24 px sides, 6 px top and bottom). Its text
 *  is `body2` (14 / 20 px) of its own, never inherited: view mode draws the box inside body2
 *  text and the edit slot's input part inside the page's body1, so an inherited line height
 *  grew every line from 20 to 21 px on E. */
export const RICHTEXT_BOX_SX = {
  minHeight: 40,
  maxHeight: '18em',
  overflowY: 'auto',
  overflowWrap: 'anywhere',
  boxSizing: 'border-box',
  border: '1px solid transparent',
  borderRadius: 1,
  pl: 1,
  pr: 3,
  py: 0.75,
  typography: 'body2',
  '& p': { m: 0, mb: 0.5 },
  '& p:last-child': { mb: 0 },
} as const;

/** The open editor on the closed box's geometry; its toolbar floats above. */
const OPEN_SX = {
  position: 'relative',
  flex: '1 1 auto',
  minWidth: 0,
  '& .ql-toolbar': {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    zIndex: 3,
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    boxShadow: 4,
    mb: 0.5,
  },
  '& .ql-container': { border: 1, borderColor: 'divider', borderRadius: 1, fontFamily: 'inherit', fontSize: 14 },
  // The closed box's text metrics (`RICHTEXT_BOX_SX`): opening moves no line.
  '& .ql-editor': { p: '6px 24px 6px 8px', maxHeight: '18em', overflowY: 'auto', typography: 'body2' },
  '& .ql-editor p': { m: 0, mb: 0.5 },
  '& .ql-editor p:last-child': { mb: 0 },
} as const;

/** The text index (Quill's: one per character, one per line end) of a point in rendered
 *  HTML — each block (p, li, div, heading) ends with a line break, as a `<br>` does. */
export function textIndexAt(root: Node, target: Node, offset: number): number {
  let index = 0;
  let found: number | null = null;
  const BLOCK = /^(P|LI|DIV|H[1-6]|BLOCKQUOTE)$/;
  const walk = (node: Node) => {
    if (found !== null) return;
    if (node === target && node.nodeType === Node.TEXT_NODE) {
      found = index + offset;
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      index += node.textContent?.length ?? 0;
      return;
    }
    if (node.nodeName === 'BR') {
      index += 1;
      return;
    }
    node.childNodes.forEach((child, i) => {
      if (node === target && i === offset && found === null) found = index;
      walk(child);
    });
    if (node === target && found === null) found = index;
    if (node !== root && BLOCK.test(node.nodeName) && node.lastChild?.nodeName !== 'BR') index += 1;
  };
  walk(root);
  return found ?? index;
}

/** The caret position under a point (`caretPositionFromPoint`, WebKit's
 *  `caretRangeFromPoint`), or null. */
function caretAt(x: number, y: number): { node: Node; offset: number } | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  const p = doc.caretPositionFromPoint?.(x, y);
  if (p) return { node: p.offsetNode, offset: p.offset };
  const r = doc.caretRangeFromPoint?.(x, y);
  return r ? { node: r.startContainer, offset: r.startOffset } : null;
}

/** The stored HTML as view mode shows it (and the editor before it opens). */
export function RichTextDisplay({ html, empty = '—' }: { html: unknown; empty?: string }) {
  const text = typeof html === 'string' ? html : '';
  return isHtmlEmpty(text) ? (
    // Words read in text.secondary; only a bare dash is disabled-grey.
    <Typography variant="body2" color={empty === '—' ? 'text.disabled' : 'text.secondary'}>
      {empty}
    </Typography>
  ) : (
    <HtmlContent html={text} sx={{ fontSize: 14 }} />
  );
}

export interface RichTextFieldProps {
  id: string;
  label: string;
  value: string;
  describedBy?: string;
  error?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** One edit; `session` groups a focus session's edits into one undo step. */
  onCommit: (html: string, session: string) => void;
  /** A new session token. */
  newSession: () => string;
  /** The session is over (the editor closed, after its last commit). */
  onSessionEnd?: () => void;
}

/** The open editor (Quill's content-editable root) as assistive tech meets it. */
function nameEditor(root: HTMLElement, { label, describedBy, error }: { label: string; describedBy?: string; error?: boolean }) {
  root.setAttribute('role', 'textbox');
  root.setAttribute('aria-multiline', 'true');
  root.setAttribute('aria-label', label);
  if (describedBy) root.setAttribute('aria-describedby', describedBy);
  else root.removeAttribute('aria-describedby');
  if (error) root.setAttribute('aria-invalid', 'true');
  else root.removeAttribute('aria-invalid');
}

export function RichTextField({
  id,
  label,
  value,
  describedBy,
  error,
  disabled,
  placeholder,
  onCommit,
  newSession,
  onSessionEnd,
}: RichTextFieldProps) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const closedRef = useRef<HTMLDivElement>(null);
  /** Where the caret goes once the editor is up (null: the end), its height until then. */
  const caret = useRef<number | null>(null);
  const [height, setHeight] = useState(40);
  const refocus = useRef(false);
  const quill = useRef<QuillInstance | null>(null);
  const session = useRef<string | null>(null);
  const pending = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  const endRef = useRef(onSessionEnd);
  endRef.current = onSessionEnd;
  const naming = useRef({ label, describedBy, error });
  naming.current = { label, describedBy, error };

  const flush = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const html = pending.current;
    pending.current = null;
    if (html !== null && session.current) commitRef.current(html, session.current);
  }, []);

  const close = useCallback(
    (giveBackFocus = false) => {
      flush();
      session.current = null;
      endRef.current?.();
      refocus.current = giveBackFocus;
      setOpen(false);
    },
    [flush],
  );

  // Closed by Escape: the focus goes back to the closed box.
  useEffect(() => {
    if (open || !refocus.current) return;
    refocus.current = false;
    closedRef.current?.focus();
  }, [open]);

  // Unmounting while open (the selection moved away) still saves the last keystrokes.
  useEffect(
    () => () => {
      const wasOpen = session.current !== null;
      flush();
      if (wasOpen) endRef.current?.();
    },
    [flush],
  );

  // The open editor's name, description and validity follow the props.
  useEffect(() => {
    if (!open) return;
    const root = quill.current?.root ?? wrapper.current?.querySelector<HTMLElement>('.ql-editor');
    if (root) nameEditor(root, { label, describedBy, error });
  }, [open, label, describedBy, error]);

  // Opened: focus the editor, the caret where the text was clicked (else at its end).
  useEffect(() => {
    if (!open) return;
    const q = quill.current;
    if (q) {
      const end = Math.max(0, q.getLength() - 1);
      q.setSelection(Math.min(caret.current ?? end, end), 0, 'api');
      if (!q.hasFocus()) q.focus();
      return;
    }
    const el = wrapper.current?.querySelector<HTMLElement>('.ql-editor');
    el?.focus();
  }, [open]);

  const start = (point?: { x: number; y: number }) => {
    if (disabled || open) return;
    const box = closedRef.current;
    const content = box?.querySelector('[data-richtext-content]');
    const at = point && content ? caretAt(point.x, point.y) : null;
    caret.current = at && content?.contains(at.node) ? textIndexAt(content, at.node, at.offset) : null;
    setHeight(box?.offsetHeight || 40);
    session.current = newSession();
    setOpen(true);
  };

  if (!open) {
    return (
      <Box
        ref={closedRef}
        id={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`Edit ${label}`}
        aria-describedby={describedBy}
        aria-disabled={disabled || undefined}
        data-richtext="closed"
        onClick={(e) => start({ x: e.clientX, y: e.clientY })}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            start();
          }
        }}
        sx={{
          ...RICHTEXT_BOX_SX,
          position: 'relative',
          flex: '1 1 auto',
          minWidth: 0,
          cursor: disabled ? 'default' : 'text',
          // The transparent border shows on hover (dashed) — view mode's geometry stays.
          borderColor: error ? 'error.main' : 'transparent',
          borderStyle: error ? 'solid' : 'dashed',
          '&:hover': { borderColor: error ? 'error.main' : 'divider' },
          '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: -1 },
        }}
      >
        <Box data-richtext-content>
          <RichTextDisplay html={value} empty={placeholder ?? 'Empty — click to write'} />
        </Box>
        <EditOutlinedIcon aria-hidden sx={{ position: 'absolute', top: 4, right: 4, fontSize: 16, color: 'text.secondary' }} />
      </Box>
    );
  }
  return (
    <Box
      ref={wrapper}
      data-richtext="open"
      onBlur={(e) => {
        // Focus moving inside (the toolbar, the link tooltip) keeps it open.
        if (wrapper.current && e.relatedTarget instanceof Node && wrapper.current.contains(e.relatedTarget)) return;
        close(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          close(true);
        }
      }}
      sx={[
        OPEN_SX,
        { '& .ql-editor': { minHeight: `${Math.max(38, height - 2)}px` } },
        error ? { '& .ql-container, & .ql-toolbar': { borderColor: 'error.main' } } : {},
      ]}
    >
      <QuillEditor
        value={value}
        placeholder={placeholder}
        onEditor={(q) => {
          quill.current = q;
          // A re-seeded editor (an outside change while open) is a new root: name it too.
          if (q) nameEditor(q.root, naming.current);
        }}
        onChange={(html) => {
          pending.current = html;
          if (timer.current !== null) window.clearTimeout(timer.current);
          timer.current = window.setTimeout(flush, RICHTEXT_COMMIT_MS);
        }}
      />
    </Box>
  );
}
